import { faClockRotateLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment } from "react";
import { Divider, Popup } from "semantic-ui-react";
import { formatZonedDateTime } from "../../../../../../components/global_components/ZonedDateTime";
import { useTimezone } from "../../../../../../hooks/useTimezone";
import ContactConnectedProfilePopup from "../../../../../contacts/components/ContactConnectedProfilePopup";
import { ContactAvatar } from "../../../../../permits/components/ContactAvatar";
import { useNotifyContentDetails } from "../../../../hooks/useNotifyContent";

const HistoryCategory = {
  Create: "Create",
  Update: "Update",
  Archived: "Archived",
  SubmittedForPublisherApproval: "SubmittedForPublisherApproval",
  PublisherRejected: "PublisherRejected",
  MassNotificationScheduled: "MassNotificationScheduled",
  MassNotificationStarted: "MassNotificationStarted",
} as const;
type HistoryCategory = (typeof HistoryCategory)[keyof typeof HistoryCategory];

const HistoryCategoryLabelLookup: Record<HistoryCategory, string> = {
  [HistoryCategory.Create]: "Draft Created",
  [HistoryCategory.Update]: "Edited",
  [HistoryCategory.Archived]: "Archived",
  [HistoryCategory.SubmittedForPublisherApproval]: "Sent for Review",
  [HistoryCategory.PublisherRejected]: "Rejected by Reviewer",
  [HistoryCategory.MassNotificationScheduled]: "Scheduled for Send",
  [HistoryCategory.MassNotificationStarted]: "Sent",
};

export function NotifyNotificationActivityLog({ notifyContentId }: { notifyContentId: number }) {
  const { notifyContent } = useNotifyContentDetails({ id: notifyContentId });
  const history = notifyContent?.history ?? [];
  const timezoneInfo = useTimezone();

  const buildButtonText = () => {
    const mostRecentEntry = history[history.length - 1];
    if (!mostRecentEntry) return "No Activity";
    const date = new Date(mostRecentEntry.createdAt);
    const dateString = formatZonedDateTime({
      value: date,
      formatString: "MMM d",
      timezoneInfo,
      disableAutoTimezoneIndicator: true,
    });

    return `${HistoryCategoryLabelLookup[mostRecentEntry.category]} ${dateString}`;
  };

  const buttonText = buildButtonText();

  return (
    <Popup
      trigger={
        <button className='tw-flex tw-cursor-pointer tw-items-center tw-gap-gg-1 tw-rounded-md tw-border-none tw-bg-transparent tw-p-gg-1 tw-text-text-secondary tw-transition-all tw-duration-75 hover:tw-bg-white-dark active:tw-bg-white-mid disabled:tw-cursor-default disabled:tw-opacity-60'>
          <FontAwesomeIcon icon={faClockRotateLeft} />
          {buttonText}
        </button>
      }
      basic
      on={["click"]}
      position='bottom center'
      offset={[0, 10]}
      hoverable
      closeOnPortalMouseLeave={false}
      className='tw-m-0 tw-min-w-72 tw-p-[21px] tw-shadow-md'
    >
      <h3 className='tw-mb-gg-5 tw-font-bold'>Activity Log</h3>
      <div>{history.length === 0 && <p className='tw-m-0'>No activity</p>}</div>
      <div className='tw-flex tw-flex-col tw-gap-gg-4'>
        {history.map((entry, index) => {
          const date = new Date(entry.createdAt);
          const dateString = formatZonedDateTime({
            value: date,
            formatString: "MMM d, h:mm a",
            timezoneInfo,
          });
          return (
            <Fragment key={entry.id}>
              <div className='tw-flex tw-w-full tw-items-center tw-justify-between'>
                <div className='tw-flex tw-flex-col'>
                  <span className='tw-font-semibold'>{HistoryCategoryLabelLookup[entry.category]}</span>
                  <span className='tw-text-base tw-text-text-secondary'>{dateString}</span>
                </div>
                {entry.person && (
                  <ContactConnectedProfilePopup
                    personId={entry.person.personId}
                    trigger={
                      <ContactAvatar
                        firstName={entry.person.firstName}
                        lastName={entry.person.lastName}
                        size='18px'
                        fontScale='8px'
                      />
                    }
                  />
                )}
              </div>
              {index < history.length - 1 && <Divider className='tw-m-0' />}
            </Fragment>
          );
        })}
      </div>
    </Popup>
  );
}
